import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, BookOpen, ArrowRight, Award } from "lucide-react";
import { downloadCertificate } from "@/lib/certificate";
import { toast } from "@/components/ui/sonner";

export default function MyLearning() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);

  useEffect(() => {
    api.get("/my/enrollments").then(({ data }) => setCourses(data));
  }, []);

  const getCertificate = async (courseId) => {
    try {
      const { data } = await api.get(`/courses/${courseId}/certificate`);
      await downloadCertificate(data);
      toast.success(t("myLearning.certSuccess"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("myLearning.certNotEligible"));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10" data-testid="my-learning">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">{t("myLearning.title")}</h1>
        <p className="text-slate-500 mb-8">{t("myLearning.subtitle")}</p>

        {courses === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : courses.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
            <BookOpen className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-4 text-slate-500">{t("myLearning.empty")}</p>
            <Button onClick={() => navigate("/courses")} className="mt-4 bg-blue-600 hover:bg-blue-700">
              {t("myLearning.browseCourses")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => (
              <Card
                key={c.course_id}
                data-testid="enrolled-course-card"
                className="overflow-hidden border-slate-200 flex flex-col"
              >
                <div className="h-36 overflow-hidden">
                  <img src={c.thumbnail} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <Badge className="w-fit bg-white/90 text-slate-700 border border-slate-200 mb-2" variant="outline">
                    {c.category}
                  </Badge>
                  <h3 className="font-heading text-lg font-bold text-slate-900">{c.title}</h3>
                  <div className="mt-4 mb-4">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-slate-500">{t("myLearning.lessonsProgress", { done: c.completed_count, total: c.lesson_count })}</span>
                      <span className="font-semibold text-slate-700">{c.progress}%</span>
                    </div>
                    <Progress value={c.progress} className="h-2" />
                  </div>
                  <Button
                    onClick={() => navigate(`/courses/${c.course_id}`)}
                    className="mt-auto bg-slate-900 hover:bg-slate-800"
                    data-testid="continue-course-button"
                  >
                    {c.progress === 100 ? t("myLearning.review") : t("myLearning.continue")} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                  {c.certificate_eligible && (
                    <Button
                      onClick={() => getCertificate(c.course_id)}
                      variant="outline"
                      className="mt-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid="mylearning-certificate-button"
                    >
                      <Award className="mr-1.5 h-4 w-4" /> {t("myLearning.certificate")}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
